import { useCallback, useEffect, useRef, useState, type SubmitEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { routePaths } from '@config/routes'
import { isAuthenticated, loginNotify, saveAuthSession, useLoginPage } from '@features/auth'
import logo from '@assets/logo.png'

const TURNSTILE_SITE_KEY =
  import.meta.env.VITE_TURNSTILE_SITE_KEY || '0x4AAAAAAEM4TcCDCvBok2au'

const TURNSTILE_POLL_MS = 50
const TURNSTILE_POLL_MAX_ATTEMPTS = 200

type TurnstileApi = {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string
      theme?: string
      size?: string
      callback?: (token: string) => void
      'expired-callback'?: () => void
      'error-callback'?: () => void
    },
  ) => string
  remove?: (widgetId: string) => void
}

function getTurnstile(): TurnstileApi | undefined {
  return (window as Window & { turnstile?: TurnstileApi }).turnstile
}

function PersonIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16" aria-hidden>
      <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6m2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0m4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4m-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10s-3.516.68-4.168 1.332c-.678.678-.83 1.418-.832 1.664z" />
    </svg>
  )
}

function LockIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16" aria-hidden>
      <path
        fillRule="evenodd"
        d="M8 0a4 4 0 0 1 4 4v2.05a2.5 2.5 0 0 1 2 2.45v5a2.5 2.5 0 0 1-2.5 2.5h-7A2.5 2.5 0 0 1 2 13.5v-5a2.5 2.5 0 0 1 2-2.45V4a4 4 0 0 1 4-4M4.5 7A1.5 1.5 0 0 0 3 8.5v5A1.5 1.5 0 0 0 4.5 15h7a1.5 1.5 0 0 0 1.5-1.5v-5A1.5 1.5 0 0 0 11.5 7zM8 1a3 3 0 0 0-3 3v2h6V4a3 3 0 0 0-3-3"
      />
    </svg>
  )
}

function LoginFormBrand() {
  return (
    <div className="login-brand" title="Venture Capital and Corporate Investments">
      <img
        src={logo}
        alt="Venture Capital and Corporate Investments"
        className="app-navbar__logo"
        width={571}
        height={100}
        decoding="async"
      />
      <div className="login-brand-accent" aria-hidden />
    </div>
  )
}

function LoginBackground() {
  return (
    <div className="login-page__bg" aria-hidden>
      <span className="login-page__orb login-page__orb--1" />
      <span className="login-page__orb login-page__orb--2" />
      <span className="login-page__orb login-page__orb--3" />
    </div>
  )
}

interface LoginIntroProps {
  showOtpUi: boolean
}

function LoginIntro({ showOtpUi }: LoginIntroProps) {
  return (
    <div className="login-intro">
      <h2 id="login-form-heading" className="welcome-title">
        {showOtpUi ? 'OTP Verification' : 'Sign In'}
      </h2>
      {showOtpUi && (
        <p className="login-subtitle">Enter the code sent to your mobile</p>
      )}
    </div>
  )
}

interface CredentialsFormProps {
  userName: string
  password: string
  loading: boolean
  onUserNameChange: (value: string) => void
  onPasswordChange: (value: string) => void
  onTurnstileToken: (token: string) => void
  onSubmit: (event: SubmitEvent<HTMLFormElement>) => void
}

function CredentialsForm({
  userName,
  password,
  loading,
  onUserNameChange,
  onPasswordChange,
  onTurnstileToken,
  onSubmit,
}: CredentialsFormProps) {
  const turnstileRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | null>(null)

  useEffect(() => {
    const el = turnstileRef.current
    if (!el) {
      return
    }

    const notifyToken = (token: string) => {
      queueMicrotask(() => onTurnstileToken(token))
    }

    const tryMount = (): boolean => {
      if (widgetIdRef.current) {
        return true
      }

      const turnstile = getTurnstile()
      if (!turnstile?.render) {
        return false
      }

      try {
        widgetIdRef.current = turnstile.render(el, {
          sitekey: TURNSTILE_SITE_KEY,
          theme: 'light',
          size: 'flexible',
          callback: notifyToken,
          'expired-callback': () => notifyToken(''),
          'error-callback': () => notifyToken(''),
        })
        return true
      } catch {
        loginNotify.turnstileUnavailable()
        return false
      }
    }

    let intervalId: number | undefined
    let attempts = 0

    if (!tryMount()) {
      intervalId = window.setInterval(() => {
        attempts += 1
        if (tryMount() || attempts >= TURNSTILE_POLL_MAX_ATTEMPTS) {
          if (intervalId) {
            window.clearInterval(intervalId)
          }
        }
      }, TURNSTILE_POLL_MS)
    }

    return () => {
      if (intervalId) {
        window.clearInterval(intervalId)
      }
      const widgetId = widgetIdRef.current
      if (widgetId) {
        getTurnstile()?.remove?.(widgetId)
        widgetIdRef.current = null
      }
    }
  }, [onTurnstileToken])

  return (
    <form className="login-form" onSubmit={onSubmit} noValidate>
      <div className="login-field-group">
        <div className="input-group custom-input-group">
          <span className="input-group-text">
            <PersonIcon />
          </span>
          <input
            id="UserName"
            name="userName"
            className="form-control"
            value={userName}
            placeholder="Enter your user ID"
            aria-label="User ID"
            onChange={(event) => onUserNameChange(event.target.value)}
            autoComplete="username"
            disabled={loading}
            required
          />
        </div>
      </div>

      <div className="login-field-group">
        <div className="input-group custom-input-group">
          <span className="input-group-text">
            <LockIcon />
          </span>
          <input
            id="Password"
            name="password"
            type="password"
            className="form-control"
            placeholder="Enter your password"
            aria-label="Password"
            value={password}
            onChange={(event) => onPasswordChange(event.target.value)}
            autoComplete="current-password"
            disabled={loading}
            required
          />
        </div>
      </div>
      <div className="login-field-group">
        <div className="login-turnstile-field">
          <div ref={turnstileRef} className="login-turnstile" />
        </div>
      </div>

      <button type="submit" className="btn login-btn w-100" disabled={loading}>
        {loading ? 'Logging in…' : 'Login'}
      </button>
    </form>
  )
}

interface OtpFormProps {
  otp: string
  loading: boolean
  onOtpChange: (value: string) => void
  onSubmit: (event: SubmitEvent<HTMLFormElement>) => void
}

function OtpForm({ otp, loading, onOtpChange, onSubmit }: OtpFormProps) {
  return (
    <form className="login-form" onSubmit={onSubmit} noValidate>
      <div className="login-field-group">
        <label className="login-label" htmlFor="Otp">
          One-time password
        </label>
        <input
          id="Otp"
          name="otp"
          className="form-control login-field"
          placeholder="Enter OTP"
          value={otp}
          onChange={(event) => onOtpChange(event.target.value)}
          autoComplete="one-time-code"
          inputMode="numeric"
          disabled={loading}
          required
        />
      </div>
      <button type="submit" className="btn login-btn w-100" disabled={loading}>
        {loading ? 'Verifying…' : 'Verify OTP'}
      </button>
    </form>
  )
}

export default function LoginPage() {
  debugger
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [userName, setUserName] = useState('')
  const [password, setPassword] = useState('')
  const [turnstileToken, setTurnstileToken] = useState('')
  const [otp, setOtp] = useState('')

  const turnstileCompletedRef = useRef(false)

  const {
    loading,
    response,
    showOtpUi,
    submitLogin,
    submitOtp,
  } = useLoginPage({
    autoLoginUserName: searchParams.get('txtbxUserName'),
    autoLoginPassword: searchParams.get('txtbxPassword'),
  })

  useEffect(() => {
    if (isAuthenticated()) {
      navigate(routePaths.welcome, { replace: true })
    }
  }, [navigate])

  useEffect(() => {
    if (response?.outcome === 'Success' && response.session) {
      saveAuthSession(response.session)
      navigate(routePaths.welcome, { replace: true })
    }
  }, [response, navigate])

  const handleTurnstileToken = useCallback((token: string) => {
    if (token) {
      turnstileCompletedRef.current = true
    } else if (turnstileCompletedRef.current) {
      loginNotify.turnstileExpired()
    }
    setTurnstileToken(token)
  }, [])

  const handleLogin = useCallback(
    (event: SubmitEvent<HTMLFormElement>) => {
      event.preventDefault()
      void submitLogin(userName, password, turnstileToken)
    },
    [submitLogin, userName, password, turnstileToken],
  )

  const handleOtpSubmit = useCallback(
    (event: SubmitEvent<HTMLFormElement>) => {
      event.preventDefault()
      void submitOtp(otp)
    },
    [submitOtp, otp],
  )

  return (
    <div className="login-page vcc-app">
      <LoginBackground />

      <div className="login-page__scroll">
        <div className="login-page__center">
          <article className="login-glass-card">
            <LoginFormBrand />

            <section className="login-form-section" aria-labelledby="login-form-heading">
              <LoginIntro showOtpUi={showOtpUi} />

              {!showOtpUi ? (
                <CredentialsForm
                  userName={userName}
                  password={password}
                  loading={loading}
                  onUserNameChange={setUserName}
                  onPasswordChange={setPassword}
                  onTurnstileToken={handleTurnstileToken}
                  onSubmit={handleLogin}
                />
              ) : (
                <OtpForm
                  otp={otp}
                  loading={loading}
                  onOtpChange={setOtp}
                  onSubmit={handleOtpSubmit}
                />
              )}
            </section>

            <footer className="footer-text">
              © <span>{new Date().getFullYear()}</span> vccipl.com All Rights Reserved. Powered by {" "}
              <span
                onClick={() => window.open("https://www.inooga.com", "_blank")}
                style={{
                  color: "#797c81",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                INOOGA
              </span>.</footer>
          </article>
        </div>
      </div>
    </div>
  )
}
